import type { SprintEmailContent } from '../types/Sprint'

/**
 * The sprint invitation body, shared so the wizard preview and the send path cannot diverge — a
 * facilitator who never opens the editor gets exactly the body it would have shown them.
 */
export type SprintInviteCopy = {
  /** Carries the literal `[Name]`, replaced with the `{{name}}` variable. */
  greeting: string
  introduction: string
  howItWorksDescription: string
  sprintScheduleTitle: string
  equipmentTitle: string
  equipmentDescription: string
  createAccountTitle: string
  /** Carries the literal `[link]`, replaced with the `{{appURL}}` variable. */
  createAccountDescription: string
  prepareTitle: string
  prepareDescription: string
  questionsText: string
  faqLink: string
  questionsOr: string
  userGuideLink: string
  closingMessage: string
  closingSignature: string
}

/** English fallback for the send path, which has no access to the browser's translations. */
export const DEFAULT_SPRINT_INVITE_COPY: SprintInviteCopy = {
  greeting: 'Hi [Name],',
  introduction:
    'Welcome to your upcoming training with Intraction. Intraction is an interactive tool that aims to strengthen your collaboration skills using simulations and data driven feedback.',
  howItWorksDescription:
    'In the Intraction game - a collaboration simulator - your actions and dialogue are captured as objective behavioural data, giving you unbiased and actionable insights on your strengths and development areas when collaborating under pressure. After each session, you will receive behavioural feedback on what’s working and what to watch out for with actionable recommendations and exercises for improvement.',
  sprintScheduleTitle: 'Your training sprint schedule contains the following activities:',
  equipmentTitle: 'Equipment setup',
  equipmentDescription: 'Laptop, Chrome browser, stable Wi-Fi, and a mouse.',
  createAccountTitle: 'Create an account and sign in',
  createAccountDescription: 'Sign up here: [link] using your work e-mail',
  prepareTitle: 'Prepare and get onboard',
  prepareDescription:
    "Sign in and follow the steps for the onboarding (takes about 10-15 minutes) before your scheduled session, preferably the day before. We advise you to do the game tutorial and if you're not used to playing video games, you can always repeat the tutorial as many times as you like. This helps ensure you are fully prepared and get the most value from the experience.",
  questionsText: 'If you have any questions, please check the',
  faqLink: 'FAQ',
  questionsOr: 'or',
  userGuideLink: 'User Guide',
  closingMessage: 'Looking forward to having you in the session.',
  closingSignature: 'Warm regards,'
}

/** `{{ACTIVITIES_BLOCK}}` is rendered server-side, where the schedule and the timezone are known. */
export const ACTIVITIES_BLOCK_TOKEN = '{{ACTIVITIES_BLOCK}}'

export function buildSprintInviteBody(copy: SprintInviteCopy): SprintEmailContent {
  const sections = [
    `<p><strong>${copy.greeting.replace('[Name]', '{{name}}')}</strong></p>`,
    `<p>${copy.introduction}</p>`,
    `<p>${copy.howItWorksDescription}</p>`,
    `<p><strong>${copy.sprintScheduleTitle}</strong></p>`,
    ACTIVITIES_BLOCK_TOKEN,
    `<p><strong>${copy.equipmentTitle}</strong></p>`,
    `<p>${copy.equipmentDescription}</p>`,
    `<p><strong>${copy.createAccountTitle}</strong></p>`,
    `<p>${copy.createAccountDescription.replace('[link]', '{{appURL}}')}</p>`,
    `<p><strong>${copy.prepareTitle}</strong></p>`,
    `<p>${copy.prepareDescription}</p>`,
    `<p>${copy.questionsText} ${copy.faqLink} ${copy.questionsOr} ${copy.userGuideLink}.</p>`,
    `<p>${copy.closingMessage}</p>`,
    `<p>${copy.closingSignature}<br/>{{facilitatorName}}</p>`
  ]

  return { body: sections.join('') }
}
